/**
 * №29 (S4, §3 ④) — WHICH SUB-VIEWS A RECORD HAS. The declaration seam.
 *
 * ## What this replaces
 *
 * S2 generated the nav's OPEN sub-nav from the route→anchor matrix: a row appears if it
 * is a registered route in the record family and declares a `navLabel`. That killed
 * hand-kept nav lists, and it left one thing wrong — **it was entity-blind**. Every row
 * in the family showed on every record type, so a contact was offered the same sub-views
 * as a matter. The moment a matter's own views (Parties, Billing, Trust, …) became routes
 * rather than in-page tabs, that stopped being cosmetic: a contact would have grown six
 * rows leading to a surface that has nothing to say about a contact.
 *
 * ## Why this is a seam and not a `if (entity === "matter")`
 *
 * The ruling on §3 ④ was explicit: *build the declaration seam, not a matter-shaped
 * hardcode.* What a record type is made of is **shell-as-data** — the same principle
 * №14 established for brand/theme/nav-order — and №23's declared-sections mechanism is
 * where it will eventually be answered from, per entity type, by the server. When that
 * lands, `declaredSubViews` changes its SOURCE and nothing else: the shape it returns
 * (ordered matrix-row keys) is already the shape a declaration would produce, and
 * `openRecordNav` never learns where the answer came from.
 *
 * Until then the declaration lives here, in one table, which is the point — one place to
 * read, one place to replace.
 *
 * ## The two rulings baked into the table below
 *
 * **Billing and Trust are TWO rows, never one "Money".** The probe sketched a single
 * Money row and that sketch is not the contract: trust money is held for someone else
 * under statutory certification, and office money is the firm's. Fusing them into one
 * nav row would put a regulated ledger and a revenue view behind the same word. The
 * probe's seven names were illustrative; the PATTERN — one navigation per record, in the
 * OPEN sub-nav — is what was locked.
 *
 * **No row without a surface behind it.** The probe also draws Tasks and Tables. Neither
 * has a matter surface, so neither is declared. That is the dead-nav ban (§3 ⑥ enforced
 * it against the shell's own greyed rows this lane; it applies to us too).
 */

/**
 * Sub-views every record type has, whatever it is.
 *
 * > **SUPERSEDED IN PART — №51 / D-CH6, 2026-08-18.** The sentence below read
 * > "`place-record` is the record itself and `chat-record` its conversation — both exist
 * > for any entity with a record surface", and `chat-record` was in both lists. David
 * > ruled the record carries **no Chat tab**: *"because the chat side window can be used
 * > at all times, the stage's chat is a little redundant."* It is D-CH0 taken to its
 * > conclusion — chat is a persistent layer of the shell, so a tab navigating to it spent
 * > one of the record's ten slots (№48 measured 818px needed in 818px available at 1440,
 * > zero slack) on a surface already on screen. **The ROUTE is untouched and still
 * > reachable** by the stage head's promote control, the work rail's button and ⌘K — all
 * > three read `conversationRouteFor`, which matches on `promotedFrom` and never consults
 * > this table. Only the TAB died.
 *
 * `place-record` is the record itself. **Process and Program are here rather than declared
 * per type on purpose**, and it is a correction worth stating: they look matter-shaped,
 * but `workflow_instances_for` is keyed on `(subject_type, subject_no)` and the shipped
 * chrome already builds process links for whatever type a run sits on — `NeedsYouPage`,
 * `StaffDeskPage` and `DeclaredListView` all construct
 * `/app/place/${subjectType || "matter"}/${no}/process`. Declaring them matter-only would
 * have removed a capability that exists, which is the opposite of what fixing
 * entity-blindness is for. A record with no instance renders an honest empty state; a row
 * that reaches an honest empty state is not a dead row.
 */
const UNIVERSAL: readonly string[] = ["place-record", "place-process", "place-program"];

/**
 * Per-entity-type sub-views, in nav order, as matrix row keys.
 *
 * Ordering is a presentation decision and so it is stated here rather than inherited from
 * the matrix's own order (which is a review artefact — the committed table's reading
 * order — and should not decide what a user sees first).
 */
const DECLARED: Readonly<Record<string, readonly string[]>> = {
  // №13's matter cockpit. These six were in-page tabs until S4 (§3 ④).
  //
  // №53.3.1 S0 (E-33) — `place-record-details` joins the set: the record's
  // declared sections (`RecordView` — Key dates, Team, Effective rates) moved
  // OFF the landing view into their own tab, last, per D-CR26's drawn order.
  // This array is DECLARATION order only — `record-tab-order.ts`'s
  // `orderRecordTabs` is what actually decides the strip's left-to-right
  // reading order (spine · joins by count · Program · Details last); nothing
  // reads this array's order as the display order any more.
  matter: [
    "place-record",
    "place-record-parties",
    "place-record-billing",
    "place-record-trust",
    "place-record-register",
    "place-record-conflicts",
    "place-record-files",
    "place-process",
    "place-program",
    "place-record-details",
  ],
};

/**
 * №53.3.1 S0 — which matrix row key a `_meta._related` JOIN entry (not one of
 * the three server-composed surface entries, which already carry their own
 * `key`) belongs to, keyed by the relationship's TARGET TYPE SLUG rather than
 * its `inverse_label` prose — a label is copy and can be reworded; a type slug
 * is the join's real identity. Scoped to matter's own four joins for S0, as
 * the related-claims.ts audit note predicts: "Relationship-based (join)
 * entries do not set this yet — S1 re-sources record-subviews.ts and can
 * extend it there." This table is that extension point, arrived early.
 */
const JOIN_TYPE_TO_ROW_KEY: Readonly<Record<string, string>> = {
  "matter-party": "place-record-parties",
  "file-meta": "place-record-files",
  "register-entry": "place-record-register",
  "conflict-check": "place-record-conflicts",
};

/** The matrix row key a `_meta._related` entry belongs to, or `undefined`
 *  when this rung declares no destination for it — same dead-nav discipline
 *  as the tab strip: a row with nowhere to go renders count-only and unlinked
 *  rather than a link to nothing. */
export function rowKeyForJoinType(typeSlug: string): string | undefined {
  return JOIN_TYPE_TO_ROW_KEY[typeSlug];
}

/**
 * The matrix row keys this entity type's record offers, in nav order.
 *
 * Total by construction: an entity type with no declaration gets the universal set, so a
 * new Tier-E type is navigable the day it is created without anyone editing this file.
 */
export function declaredSubViews(entity: string | undefined): readonly string[] {
  if (!entity) return UNIVERSAL;
  return DECLARED[entity] ?? UNIVERSAL;
}

/**
 * Is `rowKey` a sub-view of `entity`'s record? Used by the record route to refuse a
 * URL that names a sub-view this type does not have — `/app/place/contact/c1/billing`
 * resolves to a real ROUTE (the patterns are generic, one per sub-view, not one per
 * type × sub-view), so without this it would render a matter's billing frame around a
 * contact. It redirects to the record instead of rendering something untrue.
 */
export function hasSubView(entity: string | undefined, rowKey: string): boolean {
  return declaredSubViews(entity).includes(rowKey);
}
